import { ProductIdFromSdkProvider } from "@lib/ProductIdManager/ProductIdFromSdkProvider";
import { ProductVariantIdFromSdkProvider } from "@lib/ProductVariantIdManager/ProductVariantIdFromSdkProvider";
import { AnalyticsEventsCollectorModule } from "@modules/analytics_events_collector/AnalyticsEventsCollectorModule";
import { BaseModule } from "@modules/base_module/BaseModule";
import { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
import { container, singleton } from "tsyringe";

import { ModuleInterface } from "@interfaces/ModuleInterface";

import SdkAnalyticsEventsFactory from "./analytics/SdkAnalyticsEventsFactory";
import CommonSdk from "./common_sdk/CommonSdk";
import { SDK_MODULES, SDK_READY } from "./constants";
import { SdkDynamicModulesManager } from "./dynamic_modules/SdkDynamicModulesManager";
import { HooksManager } from "./hooks/HooksManager";
import SdkMethodsCollector from "./methods/SdkMethodsCollector";
import Sdk from "./Sdk";
import { SdkInitOptions } from "./types";

interface SdkModuleExports {
    sdk: Sdk;
    sdkMethodsCollector: SdkMethodsCollector;
    sdkDynamicModulesManager: SdkDynamicModulesManager;
}

@singleton()
export class SdkModule implements ModuleInterface<SdkModuleExports> {
    exports: SdkModuleExports;

    public constructor(initApp?: (options: SdkInitOptions) => void) {
        const { eventsCollector, isEventsSenderStarted$ } = container.resolve(
            AnalyticsEventsCollectorModule
        ).exports;

        const sdkAnalyticsEventsFactory = new SdkAnalyticsEventsFactory(
            navigator.userAgent,
            location.href,
            __APP_NAME__
        );

        const baseModule = container.resolve(BaseModule);
        const hooksManager = container.resolve(HooksManager);

        const sdkDynamicModulesManager = new SdkDynamicModulesManager(
            container.resolve(DynamicImportModule)
        );

        const sdk = new Sdk(
            container.resolve(AnalyticsEventsCollectorModule),
            baseModule,
            sdkAnalyticsEventsFactory,
            hooksManager,
            sdkDynamicModulesManager,
            initApp
        );

        const sdkMethodsCollector = new SdkMethodsCollector(
            eventsCollector,
            sdkAnalyticsEventsFactory
        );

        sdk.registerModule(
            SDK_MODULES.COMMON,
            new CommonSdk(
                sdk,
                container.resolve(ProductIdFromSdkProvider),
                container.resolve(ProductVariantIdFromSdkProvider),
                eventsCollector,
                sdkAnalyticsEventsFactory,
                isEventsSenderStarted$
            )
        );

        if (!window.gw) {
            window.gw = {};
        }

        if (!window.gw.sdk) {
            window.gw.sdk = sdk;
            eventsCollector.pushEvent(
                sdkAnalyticsEventsFactory.createSdkStarted()
            );
        }

        // This timeout is needed to ensure that SDK object correctly registered in DiContainer, before SDK_READY event is dispatched
        // Without this timeout, handlers for SDK_READY will be executed synchronously, before SDK object is registered
        setTimeout(() => {
            document.dispatchEvent(new Event(SDK_READY));
        }, 0);

        this.exports = {
            sdk,
            sdkMethodsCollector,
            sdkDynamicModulesManager,
        };
    }

    registerProviders() {
        return {};
    }
}
